import { newRequest } from "./complexRequest"
import { ComplexRequestOption, ResponseType } from "./type"

/** 模型类型：1 图片模型，2 视频模型 */
export enum AssetModelType {
  Image = 1,
  Video = 2,
}

/**
 * 视频支持的生成场景：
 * - t2v  文生视频
 * - fl2v 首尾帧生视频
 * - r2v  参考图生视频
 * - ff2v 首帧生视频
 */
export type VideoScene = "t2v" | "fl2v" | "r2v" | "ff2v"

/** 视频单个时长档位：价格与免费额度 */
export interface VideoSecondOption {
  /** 时长（秒） */
  second: number
  /** 该时长的价格（星币） */
  star_num: number
  /** 免费总次数 */
  free_times: number
  /** 剩余免费次数（已按当前用户身份折算） */
  free_remaining: number
  /** 免费额度是否仅限 VIP */
  free_vip_only: boolean
}

export interface VideoSceneSupport {
  scene: VideoScene
  /** 价格（星币），为最低档时长的价格 */
  star_num: number
  /** 最大参考图数量 */
  max_images: number
  /** 支持的时长（秒） */
  seconds?: number[] | null
  /** 各时长档位的价格与免费额度 */
  second_options?: VideoSecondOption[] | null
  /** 连续时长区间下界（配置后可传区间内任意整数秒；未启用为空/0） */
  second_min?: number | null
  /** 连续时长区间上界 */
  second_max?: number | null
  /** 区间模式每秒单价（用于任意秒数价格估算） */
  second_price?: number | null
}

export interface VideoSupport {
  size: string
  scenes: VideoSceneSupport[]
  /** 折扣系数 0–100；0 = 无折扣，展示折扣幅度为 100 - off */
  off?: number
}

export interface ImageSupport {
  size: string
  /** 价格（星币） */
  star_num: number
  /** 最大参考图数量 */
  max_images: number
  /** 折扣系数 0–100；0 = 无折扣，展示折扣幅度为 100 - off */
  off?: number
}

/** 折扣系数是否生效（0 / 100 / 缺省 = 无折扣） */
export function isDiscountOff(off?: number): off is number {
  return typeof off === "number" && off > 0 && off < 100
}

/** 按折扣系数折算星币价：折扣价 = ceil(原价 * off / 100) */
export function applyStarOff(price: number, off?: number): number {
  return isDiscountOff(off) ? Math.ceil((price * off) / 100) : price
}

/** 从一组折扣系数中取优惠力度最大者（实付比例最低） */
export function pickBestOff(offs: Array<number | undefined>): number {
  const valid = offs.filter(isDiscountOff)
  return valid.length ? Math.min(...valid) : 0
}

/** 模型外层展示用：各分辨率折扣中优惠力度最大的一档 */
export function getModelBestOff(model: {
  off?: number
  image_support?: Array<{ off?: number }> | null
  video_support?: Array<{ off?: number }> | null
}): number {
  const fromSupports = pickBestOff([
    ...(model.image_support ?? []).map((s) => s.off),
    ...(model.video_support ?? []).map((s) => s.off),
  ])
  return fromSupports || (isDiscountOff(model.off) ? model.off : 0)
}

/** 模型预设提示词（用于输入框下方的推荐提示词） */
export interface AssetModelPrompt {
  title: string
  content: string
  /** 可选参考图，点击预设时一并回显到生成框 */
  ref_images?: { url: string }[] | null
}

export interface AssetModel {
  id: number
  name: string
  description: string
  /** 模型 icon 图片 */
  ref_images: string[] | null
  type: AssetModelType
  source_url?: string
  /** 预设提示词列表 */
  prompts?: AssetModelPrompt[] | null
  /** 视频模型支持的分辨率与场景 */
  video_support?: VideoSupport[] | null
  /** 图片模型支持的分辨率与最大图片数 */
  image_support?: ImageSupport[] | null
  /** 热门/推荐：名称后展示火花 */
  is_fire?: boolean
  /** @deprecated 折扣已下沉到 image_support / video_support.off，仅作缺省回退 */
  off?: number
}

export interface AssetStyle {
  id: number
  name: string
  description: string
  ref_images: string[] | null
  type: AssetModelType
  prompt: string
  /** 封面图 */
  cover_image?: string
  /** 示例素材（图片或视频 mp4） */
  source_url?: string
}

export interface AssetModelsData {
  models: AssetModel[]
  styles: AssetStyle[]
}

// 获取生成资源的模型与风格配置
export const getAssetModels = async (
  params?: any,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<AssetModelsData>>(
    {
      url: "/api/go/asset/web_models",
      params,
      ...option,
    },
    useFetch,
  )
}

/** 图片用途：参考图 / 首帧 / 尾帧 */
export type RefImageUsingType = "reference_image" | "first_image" | "tail_image"

/** 图片来源：素材库 / 角色机器人 / 上传 */
export type ContentSource = "assets" | "robot" | "upload"

/** 创建任务时的图片项：统一承载参考图、首帧、尾帧 */
export interface CreateRefImage {
  url: string
  /** 与 prompt 中的 @tag 提及一一对应 */
  tag: number
  using_type: RefImageUsingType
  content_source: ContentSource
  /** 来源内容 id：素材=product_id，角色=robot_id，上传=0 */
  content_id: number
}

export interface CreateAssetBody {
  /** 1 图片 2 视频 */
  type: AssetModelType
  /** 来源标记：创作 Agent 场景传 100（后端据此对 Agent 生图不重复发通知），普通生成留空 */
  from_type?: number
  prompt: string
  /** 角色机器人 id，0 表示未启用 */
  robot_id: number
  style_id: number
  model_id: number
  /** 图片列表：参考图 / 首帧 / 尾帧，通过 using_type 区分 */
  ref_images: CreateRefImage[]
  /** 分辨率档位：视频如 "720p"（取自 video_support[].size）；图片如 "1k"/"2k"（取自 image_support[].size），各分辨率价格不同 */
  resolution?: string
  /** 视频时长秒数（视频生成时传），取自 second_options[].second；连续区间模型可传区间内任意整数秒 */
  seconds?: number
  /** 图片宽高比（仅图片生成时传）：1:1 / 3:4 / 4:3 / 9:16 / 16:9，空=后端默认 9:16 */
  image_ratio?: string
  /** 视频宽高比（仅视频生成时传）：9:16 / 16:9 / 1:1 / 4:3 / 3:4，空=后端默认 9:16 */
  video_ratio?: string
}

/** 重新生成：在创建参数基础上附带原资产 product_id */
export interface RegenerateAssetBody extends CreateAssetBody {
  product_id: number
}

/** 提示词优化入参：提示词 + 参考图详情 + 模型/分辨率/时长 */
export interface OptimizePromptBody {
  prompt: string
  ref_images: (CreateRefImage & { local_image_path?: string })[]
  model_id: number
  /** 分辨率，如 "720p" */
  resolution?: string
  /** 视频时长秒数 */
  second?: number
}

// AI 优化提示词
export const optimizePrompt = async (
  data: OptimizePromptBody,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ prompt?: string } | string>>(
    {
      url: "/api/go/asset/optimize_prompt",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

/** 资产列表筛选类型：0 全部 1 图片 2 视频 */
export enum AssetListType {
  All = 0,
  Image = 1,
  Video = 2,
}

export interface AssetListBody {
  type: AssetListType
  /** 0=全部 1=只看生成成功 */
  only_success?: 0 | 1
  page: number
  page_size: number
}

/** 资产生成状态 */
export enum AssetProductStatus {
  /** 待处理 */
  Pending = 0,
  /** 生成中（视频异步任务已提交） */
  Processing = 1,
  /** 成功 */
  Success = 2,
  /** 失败 */
  Failed = -1,
  /** 需要到 web 端生成 */
  WebGenerate = -2,
}

/** 资产列表项 */
export interface AssetListItem {
  /** 1 图片 2 视频 */
  type: AssetModelType
  from_type: number
  prompt: string
  /** 参考图 / 首帧 / 尾帧混合列表，通过 using_type 区分（旧数据可能是纯 url 字符串数组） */
  ref_images: (CreateRefImage | string)[] | null
  robot_info: {
    robots_id: number
    robot_name: string
    avatar: string
    background_image: string
  } | null
  /** 0 表示未绑定角色 */
  robot_id: number
  product_id: number
  model_id: number
  style_id: number
  /** 视频分辨率，如 "720p"（创建时写入，详情/列表回显） */
  resolution?: string
  /** 视频时长秒数（创建时写入，详情/列表回显） */
  seconds?: number
  image_url: string
  video_url: string
  video_url_mpeg: string
  video_url_mpeg_watermark: string
  status: AssetProductStatus
  nsfw: boolean
}

export interface AssetListData {
  total: number
  page: number
  list: AssetListItem[] | null
}

// 获取资产列表（图片 / 视频）
export const getAssetList = async (
  data: AssetListBody,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<AssetListData>>(
    {
      url: "/api/go/asset/list",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

// 删除资产（批量）
export const deleteAssets = async (
  data: { product_ids: number[] },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/api/go/asset/delete",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

// 获取单个资产详情（用于分享链接回显生成参数）
export const getAssetDetail = async (
  data: { product_id: number },
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  // 出错时（如资产不存在）data 内会带业务错误码 code（如 10404）
  return newRequest<ResponseType<AssetListItem & { code?: number }>>(
    {
      url: "/api/go/asset/detail",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

// 创建生成任务（图片 / 视频）
export const createAsset = async (
  data: CreateAssetBody,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/api/go/asset/create",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

/** 资产发布请求（发布到圈子/故事书） */
export interface PublishAssetBody {
  product_id: number
  /** 标题（过平台机审） */
  title: string
  /** 简介（过平台机审；Go 侧 JSON 字段名为 desc） */
  desc?: string
  /** 0 不公开 1 公开（缺省按公开） */
  is_public?: 0 | 1
}

/**
 * 发布资产到圈子（故事书）：仅视频类型且创建时绑定了角色（robot_id != 0）的资产可发布，
 * nsfw 分级继承绑定角色。文案审核不过时 data.code = 50001（资产未发布，可改文案重试）。
 */
export const publishAsset = async (
  data: PublishAssetBody,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<{ success?: boolean; story_book_id?: string; code?: number }>>(
    {
      url: "/api/go/asset/publish",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}

// 重新生成任务（基于已有资产）
export const regenerateAsset = async (
  data: RegenerateAssetBody,
  option?: ComplexRequestOption,
  useFetch = true,
) => {
  return newRequest<ResponseType<any>>(
    {
      url: "/api/go/asset/regenerate",
      data,
      method: "post",
      ...option,
    },
    useFetch,
  )
}
